import {jsPDF} from "jspdf";
import './Assistant-Bold-bold.js';
import './Assistant-Regular-normal.js';
const TEXT_PADDING = 3;
const TITLE_PADDING = 15;
const BLOCK_PADDING = 5;
const BLOCK_MARGIN = 5;

const calculateBlockDimensionsForLandscape = (block, maxTextWidth, pageHeight, numberOfColumns, fontSize) => {
  // Create a jsPDF instance in landscape mode.
  const doc = new jsPDF("landscape", "mm", "a4");
  doc.setFontSize(14);

  // Prepare an array to simulate the columns.
  // Each column will store:
  //   - its accumulated height,
  //   - and an array of exercises placed into it.
  const columns = [];
  for (let i = 0; i < numberOfColumns; i++) {
    columns.push({ exercises: [], height: 0, width: 0});
  }


  doc.setFont("Assistant-Regular", "normal")
  // -- Column 1: Place the title.
  const titleText = `${block.title}`;
  const titleDimensions = doc.getTextDimensions(titleText, { maxWidth: maxTextWidth });
  const titleHeight = titleDimensions.h;
  // The first column begins with the title height and padding.
  columns[0].height = titleHeight + TITLE_PADDING + 5;

  // Keep track of which column we are currently filling.
  let currentColumn = 0;

  doc.setFontSize(fontSize);
  // -- Process each exercise.
  for (const exercise of block.lines) {
    doc.setFont("Assistant-Bold", "bold")

    const exerciseText = `${exercise.name}`;
    const exDimensions = doc.getTextDimensions(exerciseText, { maxWidth: maxTextWidth });

    doc.setFont("Assistant-Regular", "normal")

    const newSunText = exercise.descriptions.map((desc) => reverseHebrewWords(desc));
    const subTextDimensions = doc.getTextDimensions(newSunText, { maxWidth: maxTextWidth});

    let exHeight = exDimensions.h + TEXT_PADDING + subTextDimensions.h;

    if (exDimensions.h !== 0){
      exHeight += TEXT_PADDING;
    }

    // Check if the exercise fits in the current column.
    if (columns[currentColumn].height + exHeight <= pageHeight) {
      // It fits, so add it.
      columns[currentColumn].exercises.push(exercise);
      columns[currentColumn].height += exHeight;
    } else {
      // It does not fit in the current column, so try moving to the next column.
      if (currentColumn + 1 < numberOfColumns) {
        currentColumn++;
        // For subsequent columns, there is no title at the top.
        columns[currentColumn].height = 0;

        // Check if the exercise fits in an empty column.
        if (exHeight <= pageHeight) {
          columns[currentColumn].exercises.push(exercise);
          columns[currentColumn].height += exHeight;
        } else {
          // Even on an empty column, the exercise is too tall.
          throw new Error("An exercise is too tall to fit in an empty column.");
        }
      } else {
        // No more columns are available to accommodate the exercise.
        throw new Error("Not enough columns to fit all exercises with the given page height.");
      }
    }
  }

  // -- Determine overall dimensions:
  // The required height is the maximum height used among the columns.
  const requiredHeight = Math.max(...columns.map(col => col.height));
  // Count how many columns were used (they may not all be used).
  const usedColumns = columns.filter(col => col.exercises.length > 0).length;
  // The required width is based on the number of used columns.
  const requiredWidth = usedColumns * maxTextWidth + usedColumns * BLOCK_MARGIN;

  return { width: requiredWidth, height: requiredHeight, columns };
};

const calculateBlockHeightAndWidthForLandscape = (block, pageHeight, numberOfColumns, blockWidth, withContinue, blocksPerPage, fontSize) => {
  for (let i = 1 ; i<= numberOfColumns; i++) {
    try {
      let {height, columns} = calculateBlockDimensionsForLandscape(block,
          (blockWidth * i) - TEXT_PADDING,
          pageHeight, 1, fontSize);

      return {blockHeight: height,
        blockWidth: blockWidth * i,
        numberOfRows: height > (pageHeight * 0.5) && withContinue ? 2 : 1,
        textWidth: (blockWidth * i) - TEXT_PADDING,
        columns: columns,
        numberOfColumnsUsed: i};
    } catch (e) {
      // console.log(e);
    }
  }

  if (withContinue) {

    let numberOfColumnsInBlock = 1;

    if (blocksPerPage === 1){
      numberOfColumnsInBlock = 4;
    } else if (blocksPerPage === 2){
      numberOfColumnsInBlock = 2;
    } else if (blocksPerPage === 3) {
      numberOfColumnsInBlock = 1;
    } else if (blocksPerPage === 4) {
      numberOfColumnsInBlock = 1
    }

    return tryToFitBlockInContinue(block, numberOfColumns, blockWidth, pageHeight, numberOfColumnsInBlock, fontSize)
  }

  throw new Error('Block is too big to fit in the page ' + block.title);
}

const tryToFitBlockInContinue = (block, numberOfAvilibleColumnsInBlockWidth, blockWidth, pageHeight, numberOfColumnsInBlock, fontSize) => {

  for (let i =1; i <= numberOfAvilibleColumnsInBlockWidth ; i++) {
    for (let j = 1 ; j <= numberOfColumnsInBlock; j++) {
      try {
        let {height, columns} = calculateBlockDimensionsForLandscape(block,
            (blockWidth - (BLOCK_PADDING * j)) /numberOfColumnsInBlock, pageHeight, j * i, fontSize);

        return {
          blockHeight: pageHeight,
          blockWidth: blockWidth * i,
          textWidth: (blockWidth - (BLOCK_PADDING * j)) /numberOfColumnsInBlock,
          numberOfRows: 2,
          columns: columns,
          numberOfColumnsUsed: i
        };
      } catch (e) {}
    }
  }

  throw new Error('Block is too big to fit in the page ' + block.title);
}

const isFirstRow = (currentColumn) => {
  return currentColumn.length === 0;
}

const getNumberOfColumnsUsed = (currentPage) => {
  return Math.min(currentPage.columns.reduce((acc, column) => {
    return acc + column.reduce((blockAcc, block) => {
      return blockAcc + (block.numberOfColumnsUsed ? block.numberOfColumnsUsed : 0);
    }, 0);
  }, 0), 4);
};

const getNumberOfColumnsWidth = (currentPage) => {

  return currentPage.columns.reduce((acc, column) => {
    return acc + column[0].blockWidth;
  }, 0);
};


const isEmptyPage = (currentPage) => {
  return currentPage.columns.length === 0;
}

const getNumberOfBlocks = (currentPage) => {
  return currentPage.columns.reduce((acc, column) => {
    return acc + column.length;
  }, 0);
};

const addBlockToPage = (block, currentPage, pageHeight, pageWidth, numberOfColumns, blocksPerPage, fontSize) => {
  // if there are no columns created, but there is a block we need to add

  const numberOfBlocks = getNumberOfBlocks(currentPage);

  if (numberOfBlocks === blocksPerPage){
    throw new Error('Page is full, need to create a new page')
  }

  const numberOfUsedColumns = getNumberOfColumnsUsed(currentPage);

  if (numberOfUsedColumns < numberOfColumns) {
    currentPage.columns.push([]);
  }


  let currentColumn = null;
  let currentColumnIdx = 0;

  if (numberOfUsedColumns < numberOfColumns) {
    currentColumn = currentPage.columns[currentPage.columns.length - 1];
  } else if (numberOfUsedColumns === numberOfColumns) {
    for (let i =0 ; i < currentPage.columns.length; i++) {
      if (currentPage.columns[i].length !== 2 && currentPage.columns[i][0].numberOfRows === 1) {
        currentColumn = currentPage.columns[i];
        currentColumnIdx = i;
        break;
      }
    }
  }

  if (currentColumn === null) {
    throw new Error('No column to add block ' + block.title);
  }


  if (isFirstRow(currentColumn)) {
    const {blockHeight, columns, blockWidth, textWidth, numberOfRows, numberOfColumnsUsed} = calculateBlockHeightAndWidthForLandscape(block,
        pageHeight, numberOfColumns - numberOfUsedColumns,
        (pageWidth - BLOCK_MARGIN * numberOfColumns) / numberOfColumns, true, blocksPerPage, fontSize);

    if (numberOfColumnsUsed + numberOfUsedColumns <= numberOfColumns) {
      currentColumn.push({
        block,
        blockHeight,
        blockWidth,
        textWidth,
        columns,
        numberOfRows,
        numberOfColumnsUsed
      });

      if (blockHeight >= 0.5 * pageHeight) {
        currentPage.columns.forEach((column) => {column.forEach((block) => {block.numberOfRows = 2})});
      }

    } else {// if the block is too big to fit in the page
      throw new Error('Block has no space in columns' + block.title);
    }

  } else {
    debugger;
    const {blockHeight, columns, blockWidth, textWidth, numberOfRows, numberOfColumnsUsed} =
        calculateBlockHeightAndWidthForLandscape(block, (pageHeight / 2) - BLOCK_MARGIN ,
            1,
            (pageWidth - BLOCK_MARGIN * numberOfColumns) / numberOfColumns,
            false, blocksPerPage, fontSize);

    if (numberOfColumnsUsed + currentColumnIdx <= numberOfColumns) {
      currentColumn.push({
        block,
        blockHeight,
        blockWidth,
        textWidth,
        columns,
        numberOfRows,
      });
  } else {
      throw new Error('Block has no space in columns' + block.title);
    }
  }
}

const fixWhiteSpaceBetweenColumns = (currentPage, pageWidth) => {
  const totalColumns = currentPage.columns.length;
  const totalColumnsWidth = getNumberOfColumnsWidth(currentPage);
  const totalWhiteSpace = pageWidth - totalColumnsWidth - (BLOCK_MARGIN * totalColumns - 1);
  const whiteSpaceForEachColumn = totalWhiteSpace / totalColumns;

  currentPage.columns.forEach((column) => {

    column[0].blockWidth += whiteSpaceForEachColumn;
    if (column[0].columns.length === 1) {
      column[0].textWidth += whiteSpaceForEachColumn - TEXT_PADDING;
    } else {
      column[0].textWidth += (whiteSpaceForEachColumn - (TEXT_PADDING * column[0].columns.length)) / column[0].columns.length;
    }
    if (column.length > 1) {
      column[1].blockWidth += whiteSpaceForEachColumn;
      if (column[1].columns.length === 1) {
        column[1].textWidth += whiteSpaceForEachColumn - TEXT_PADDING;
      } else {
        column[1].textWidth += (whiteSpaceForEachColumn - (TEXT_PADDING * column[0].columns.length)) / column[0].columns.length;
      }
    }
  });
}

const fixWhiteSpaceBetweenRows = (currentPage, pageHeight) => {
  currentPage.columns.forEach((column) => {
    if (column.length === 1) {
      column[0].blockHeight = pageHeight;
    } else if (column.length === 2) {
      column.forEach((block) => {
        block.blockHeight = (pageHeight / 2) - (BLOCK_MARGIN /2);
      });
    }
  });
};

const fixWhiteSpace = (currentPage, pageHeight, pageWidth) => {
  fixWhiteSpaceBetweenColumns(currentPage, pageWidth);
  fixWhiteSpaceBetweenRows(currentPage, pageHeight);
}

const addBlockToPageLandscape = (block, pages, blocksPerPage, pageHeight, pageWidth, fontSize) => {
  const currentPage = pages[pages.length - 1];
  const maxNumberOfColumns = Math.min(blocksPerPage, 4)

  if (isEmptyPage(currentPage)) {
    try {
      addBlockToPage(block, currentPage, pageHeight, pageWidth, maxNumberOfColumns, blocksPerPage, fontSize)
    } catch (e) {
      console.log("Can not add block " + block.title + " to page");
      throw e
    }
  } else {
    try {
      addBlockToPage(block, currentPage, pageHeight, pageWidth, maxNumberOfColumns, blocksPerPage, fontSize)
    } catch (e) {

      if (currentPage.columns[currentPage.columns.length-1].length === 0){
        currentPage.columns.pop();
      }
      fixWhiteSpace(currentPage, pageHeight, pageWidth);

      pages.push({
        columns: []
      })
      return addBlockToPageLandscape(block, pages, blocksPerPage, pageHeight, pageWidth, fontSize);
    }
  }
}

const getBlocksLocationPerPageForLanscape = (blocks, blocksPerPage, pageHeight, pageWidth, fontSize) => {
  debugger;
  const pages = [];
  // add the first page
  //Add a new Page
  pages.push({
    columns: []
  })

  console.log("Starting to pring blocks")
  blocks.forEach((block, index) => {
    addBlockToPageLandscape(block, pages, blocksPerPage, pageHeight, pageWidth, fontSize);
  });


  fixWhiteSpace(pages[pages.length - 1], pageHeight, pageWidth);
  return pages;
}

const drawBlock = (doc, block, xOffset, yOffset, fontSize) => {
  doc.setTextColor("#000000")
  const radius = 5; // Border radius for rounded rectangles
  // Draw the block's background (rounded rectangle)
  doc.setFillColor(255, 255, 255); // White background for blocks
  console.log("yOffset", yOffset, "block.width", block.width)

  doc.roundedRect(xOffset, yOffset, block.blockWidth, block.blockHeight , radius, radius, 'F');

  // Set initial column offset and height trackers
  let currentColumnXOffset = xOffset; // Track current X position for text
  let currentColumnHeight = 0; // Track used height in the current column



  // Title Logic
  const renderTitle = () => {
    doc.setFontSize(14);
    doc.setFont("Assistant-Regular", "normal", )
    const title = block.block.title;
    const titleDimensions = doc.getTextDimensions(title, { maxWidth: block.textWidth});
    doc.text(reverseHebrewWords(title), currentColumnXOffset + TEXT_PADDING, yOffset + 10, { maxWidth: block.textWidth});
    return (titleDimensions.h) + TITLE_PADDING; // Return height used by the title
  };

  const blockHeight = yOffset + 10;
  let textYOffset = yOffset + renderTitle(); // Render title in the first column


  // Remaining height after the title


  doc.setFontSize(fontSize);
  let i = 0;
  block.columns.forEach((column) => {
    column.exercises.forEach((exercise) => {
      doc.setFont("Assistant-Bold", "bold")
      const text = reverseHebrewWords(`${exercise.name}`);
      const textDimensions = doc.getTextDimensions(text, { maxWidth: block.textWidth});

      // Draw the text in the current column
      doc.text(text, currentColumnXOffset + TEXT_PADDING, textYOffset, { maxWidth: block.textWidth})

      doc.setFont("Assistant-Regular", "normal")

      const newSunText = exercise.descriptions.map((desc) => reverseHebrewWords(desc));
      const subTextDimensions = doc.getTextDimensions(newSunText, { maxWidth: block.textWidth});

      // Draw the text in the current column
      doc.text(newSunText, currentColumnXOffset + TEXT_PADDING, textYOffset + textDimensions.h, { maxWidth: block.textWidth})


      let textHeight = textDimensions.h + subTextDimensions.h; // Height for this text line with padding

      if (subTextDimensions.h !== 0){
        textHeight += TEXT_PADDING;
      }

      // Update offsets and column height
      textYOffset += textHeight;
      currentColumnHeight += textHeight;
    });

    currentColumnXOffset += (block.blockWidth - (TEXT_PADDING * block.columns.length))/block.columns.length ; // Add spacing for new column
    currentColumnHeight = 0; // Reset current column height
    textYOffset = blockHeight;
    if (i < block.columns.length - 1 && block.columns.length > 1) {
      doc.setLineDash([2, 2], 0); // [dot size, space size], phase 0
      doc.line(currentColumnXOffset + 2, blockHeight, currentColumnXOffset + 2, blockHeight + block.blockHeight - 20);  // x1, y1, x2, y2

      // doc.setFillColor(0, 0, 0); // White background for blocks
      // doc.roundedRect(currentColumnXOffset + 2, blockHeight, 1, block.blockHeight - 10
      //     - 5, radius, radius, 'F');
      currentColumnXOffset+=1;
    }
    i=i+1;
  });
};

function containsHebrew(text) {
  return /[\u0590-\u05FF]/.test(text);
}
function isAllHebrew(text) {
  const hebrewOnly = /^[\u0590-\u05FF]+$/;
  return hebrewOnly.test(text);
}

const nonHebrewStart = /^[^\u0590-\u05FF]/;
function endsWithHebrew(text) {
  return /[\u0590-\u05FF]$/.test(text.trim());
}

function startsWithNonHebrew(text) {
  return nonHebrewStart.test(text);
}

function lastAlphabetCharIsHebrew(text) {
  for (let i = text.length - 1; i >= 0; i--) {
    const char = text[i];
    if (/[a-zA-Z]/.test(char)) {
      return false; // Found an English letter first
    }
    if (/[\u0590-\u05FF]/.test(char)) {
      return true; // Found a Hebrew letter first
    }
  }
  return false; // No alphabetic character found
}

function firstAlphabetCharType(text) {
  for (const char of text) {
    if (/[a-zA-Z]/.test(char)) {
      return false;
    }
    if (/[\u0590-\u05FF]/.test(char)) {
      return true;
    }
  }
  return false; // No alphabetic character found
}

function splitAndProcessText(text) {
  debugger;
  // Regular expressions
  const hebrewRegex = /[\u0590-\u05FF]/;
  const splitRegex = /([\u0590-\u05FF\s]+|[a-zA-Z0-9\s#]+)/g;


  // Split text into Hebrew and English segments
  let segments = text.match(splitRegex);

  if (!segments) return text; // Return original if nothing matches

  return segments
      .map(segment => {
        if (hebrewRegex.test(segment)) {
          // Reverse the order of Hebrew words
          return segment.trim().split(/\s+/).reverse().join(" ").split(" ")
              .map((word) => (word.split("").reverse().join("") ))
              .join(" ")
        } else {
          return segment.trim().split(/\s+/).reverse().join(" ").split(" ")
              .map((word) => (word.split("").reverse().join("") ))
              .join(" ")
        }
      })
      .join(" ");
}

const reverseHebrewWords = (text) => {
  console.log("firstAlphabetCharType(text)", firstAlphabetCharType(text), text);

  if (firstAlphabetCharType(text))
    return splitAndProcessText(text);
  else
    return text;
};

const UseLandscape = (blocks, blocksPerPage, image, dateStr, workoutName, fontSize) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');

  debugger;
  console.log(doc.getFontList()); // This should show "NotoSansHebrew"

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();


  // const pages = getBlocksLocationPerPageForPortrait(blocks, 8, pageHeight - 50, pageWidth -20)
  const pages = getBlocksLocationPerPageForLanscape(blocks, blocksPerPage, pageHeight - 30, pageWidth -20, fontSize)

    pages.forEach((page, pageIndex) => {
      if (pageIndex > 0) {
        doc.addPage();
      }

      // Draw background for each page
      doc.setFillColor(29, 29, 29);
      doc.rect(0, 0, pageWidth, pageHeight, 'F'); // 'F' for filled background
      doc.addImage(image, "PNG", 10, 5, 15, 15);
      doc.setFont("Assistant-Regular", "normal")
      doc.setTextColor("#e0e0e0")
      doc.setFontSize(18);
      debugger;
      doc.text(reverseHebrewWords(workoutName), 30, 10);
      doc.setFont("Assistant-Regular", "normal")
      doc.setFontSize(16);
      doc.text(dateStr, 30, 18);


      const columnSpacing = 5; // Space between columns
      const rowSpacing = 5; // Space between rows

      // Iterate columns first for portrait
      let xOffset = 10;

      page.columns.forEach((column) => {
        let yOffset = 25;

        column.forEach((block, index) => {
          drawBlock(doc, block, xOffset, yOffset, fontSize);
          yOffset += block.blockHeight + rowSpacing;
        });

        // Increment xOffset for the next column
        xOffset += column[0].blockWidth + columnSpacing;
      });

    });

      // Save the PDF
  doc.save('Workout_Plan.pdf');
};

export default UseLandscape;
